"use client";

import type { CDescriptivePageContent } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import {
  cDescriptivePageTranslations,
} from "./CDescriptivePage.i18n";
import styles from "./CDescriptivePage.module.css";

/**
 * CDescriptivePage — visitor view komponentu `c_descriptive_page` (Strona opisowa).
 *
 * **Data source:** `C_DESCRIPTIVE_PAGE_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   {
 *     id, symbol, title, metatags,
 *     paragraphs: ParagraphData[]   // discriminated union per layout
 *   }
 *   ```
 *
 * **Renderowanie akapitów:** delegujemy do `<ParagraphRenderer>` z
 * `@/components/cms/Paragraphs/` — system-wide reusable renderer obsługujący
 * wszystkie 22 layouty (text, text+images, 2/3 kolumny, prezentacja, mapa,
 * formularz, boksy, posty bloga). Każdy nowy moduł/okno może użyć tego samego
 * renderera.
 *
 * **Page title (`<h1>`):** renderuje WRAPPER (`CDefault` w
 * `components/cms/registry/variants.tsx`) — komponent zawartości BEZ
 * świadomości że jest wewnątrz card'a. Po cutover 2026-05-27 dispatch
 * lambda dla `c_descriptive_page` wrappuje content w `<CDefault title>`.
 *
 * **Security:** `<HtmlText>` w rendererze używa `dangerouslySetInnerHTML` — OK
 * tu bo content pochodzi z admin panelu Imperial (trusted), nie z user input.
 */
export function CDescriptivePage({ content }: CDescriptivePageProps) {
  const t = useT(cDescriptivePageTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  return (
    <ParagraphRenderer
      paragraphs={Array.isArray(content.paragraphs) ? content.paragraphs : []}
      emptyMessage={t.emptyContent}
    />
  );
}

export type CDescriptivePageProps = {
  content: CDescriptivePageContent;
};
